"use client";

import { useAPI } from "@/lib/api";
import { cn } from "@/lib/utils";
import assetUrl from "@/vars/assetUrl";
import useEmblaCarousel from "embla-carousel-react";
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InfoIcon,
  PlusIcon,
  ShoppingCartIcon,
  XIcon,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import useSWR from "swr";
import Button from "./Button";
import MarkdownFormat from "./MarkdownFormat";
import { useCartStore, useCartStoreState } from "./microsite/MicrositeCart";
import ModalComponent, { useModal } from "./Modal";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ShopItemUserReview } from "./ShopItemReview";
import { Skeleton } from "./ui/skeleton";

export default function ShopItemComponent({
  itemId,
  id,
  itemStyle,
  data: _data,
}: {
  itemId?: string;
  id: string;
  itemStyle: string;
  data?: any;
}) {
  const api = useAPI();
  const modalState = useModal();
  const carts = useCartStore((s) => s.carts);
  const addItem = useCartStore((s) => s.add);
  const setShowCart = useCartStoreState((s) => s.setShowCart);
  const prevProductState = useRef<string | null>(null);
  const [productState, setProductState] = useState<"IDLE" | "VIEW" | "CART">(
    "IDLE"
  );
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const record = (type: string) => {
      console.log("record", type);
      api.post("/microsite/shop/tr", { p: itemId, t: type });
    };

    if (prevProductState.current === "IDLE" && productState === "VIEW") {
      record("VIEW");

      //@ts-ignore
      if (typeof window.fbq !== "undefined") {
        //@ts-ignore
        window.fbq("track", "ViewContent", {
          content_ids: [itemId],
          content_name: data.name,
          content_type: "product",
          value: data.price.idr,
          currency: "IDR",
        });
      }
    }
    if (prevProductState.current === "VIEW" && productState === "CART") {
      record("CART");

      //@ts-ignore
      if (typeof window.fbq !== "undefined") {
        //@ts-ignore
        window.fbq("track", "AddToCart", {
          content_ids: [itemId],
          content_name: data.name,
          content_type: "product",
          value: data.price.idr,
          currency: "IDR",
        });
      }
    }

    prevProductState.current = productState;
  }, [productState]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setCurrentIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();
  const scrollTo = (index: number) => emblaApi && emblaApi.scrollTo(index);

  const {
    data: __data,
    isLoading,
    error,
  } = useSWR(itemId ? "/microsite/shop/items/" + itemId : null, {
    revalidateOnFocus: true,
    shouldRetryOnError: false,
  });

  const data = useMemo(() => {
    return _data || __data?.data;
  }, [_data, __data]);

  const isExistsOnCart = carts?.findIndex((m) => m?.id === data?.id) !== -1;

  const onCheckout = () => {
    setProductState("CART");
    if (isExistsOnCart) {
      modalState.close();
      setShowCart();
      return;
    }
    addItem({ id: data.id, qty: 1 });
    modalState.close();
    toast.success("Added to cart!");
    setShowCart();
  };

  if (isLoading) {
    return (
      <div className="bg-background p-3 rounded-md">
        <Skeleton className="pb-[100%] rounded-md w-full" />
        <div className="mt-4">
          <Skeleton className="w-9/12 h-6" />
          <Skeleton className="mt-3 w-4/12 h-3" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="relative bg-white p-3 rounded-md text-foreground">
        <div className="pb-[100%]"></div>
        <div className="absolute inset-0 flex flex-col justify-center items-center">
          <InfoIcon className="mb-2 size-6" />
          <p>Item not available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 bg-background rounded-md overflow-hidden">
      <div className="relative">
        <div
          className="relative bg-slate-200 overflow-hidden embla"
          ref={emblaRef}
        >
          <div className="flex embla__container">
            {data?.images.map(({ image }, index) => {
              return (
                <div
                  key={index}
                  className="flex-[0_0_100%] min-w-0 embla__slide"
                >
                  <div
                    className={cn(
                      "bg-slate-100 bg-no-repeat bg-center pb-[100%] w-full bg-cover"
                    )}
                    style={{
                      backgroundImage: `url(${assetUrl + image})`,
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
        {data.images.length > 1 && itemStyle === "" && (
          <div className="right-0 bottom-3 left-0 z-20 absolute flex justify-center items-center gap-2">
            {data.images.map((_, index) => (
              <button
                key={index}
                className={cn(
                  "rounded-full size-2 cursor-pointer",
                  index === currentIndex ? "bg-white" : "bg-white/50"
                )}
                onClick={() => scrollTo(index)}
              />
            ))}
          </div>
        )}

        <div
          className={cn(
            "absolute top-0 right-1 bottom-0 left-1 flex justify-between items-center pointer-events-none",
            "z-50",
            data.images.length > 1 ? "" : "opacity-0"
          )}
        >
          <div>
            <button
              onClick={scrollPrev}
              className="flex justify-center items-center bg-white rounded-full size-7 pointer-events-auto"
            >
              <ChevronLeftIcon className="size-5 text-gray-800" />
            </button>
          </div>
          <div>
            <button
              onClick={scrollNext}
              className="flex justify-center items-center bg-white rounded-full size-7 pointer-events-auto"
            >
              <ChevronRightIcon className="size-5 text-gray-800" />
            </button>
          </div>
        </div>

        <div className="right-2 bottom-2 absolute bg-black rounded-md">
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                {isExistsOnCart ? (
                  <Button
                    variant="blue"
                    onClick={onCheckout}
                    className="size-8 text-sm"
                  >
                    <CheckIcon />
                  </Button>
                ) : !data.is_unlimited && data.stock <= 0 ? (
                  <Button
                    variant="secondary"
                    disabled
                    className="size-8 text-sm"
                  >
                    <XIcon />
                  </Button>
                ) : (
                  <Button
                    variant="green"
                    onClick={onCheckout}
                    className="size-8 text-sm"
                  >
                    <PlusIcon />
                  </Button>
                )}
              </TooltipTrigger>
              <TooltipContent align="end">
                {isExistsOnCart ? "Already in Cart" : "Checkout"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="flex-1 p-3 text-foreground">
        <h3
          className="font-bold hover:underline cursor-pointer"
          onClick={() => {
            setProductState("VIEW");
            modalState.open();
          }}
        >
          {data?.name}
        </h3>
        {!data?.is_discount ? (
          <p>Rp {Intl.NumberFormat("id").format(data?.price?.idr)}</p>
        ) : (
          <div>
            <p className="font-semibold">
              Rp {Intl.NumberFormat("id").format(data?.discount_amount?.idr)}
            </p>
            <p className="text-foreground/80 text-sm line-through">
              Rp {Intl.NumberFormat("id").format(data?.price?.idr)}
            </p>
          </div>
        )}

        <p
          className={cn(
            "mt-1 text-foreground/50 text-xs",
            !data.is_unlimited && data.stock <= 0 ? "text-red-500" : ""
          )}
        >
          {data.is_unlimited ? "Unlimited" : `Stock: ${data.stock || 0}`}
        </p>
      </div>

      <ModalComponent
        {...modalState}
        drawer
        className="!max-w-3xl font-worksans"
      >
        {/* <ModalHeader {...modalState}></ModalHeader> */}
        <div className="-mt-4 sm:min-h-[80vh]">
          <button
            className="top-4 right-4 z-40 absolute flex justify-center items-center bg-white rounded-full size-12"
            onClick={() => modalState.close()}
          >
            <XIcon className="size-6" />
          </button>
          <ShopItemDetailComponent
            data={data}
            isExistsOnCart={isExistsOnCart}
            onCheckout={onCheckout}
          />
        </div>
      </ModalComponent>
      {/* {JSON.stringify(data)} */}
    </div>
  );
}

export function ShopItemDetailComponent({ data, isExistsOnCart, onCheckout }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setCurrentIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();
  const scrollTo = (index: number) => emblaApi && emblaApi.scrollTo(index);

  return (
    <>
      <div className="relative flex justify-center bg-black -mx-4 rounded-t-lg min-h-[40dvh]">
        <div
          className="relative bg-black h-full overflow-hidden embla"
          ref={emblaRef}
        >
          <div className="flex embla__container">
            {data?.images.map(({ image }, index) => {
              return (
                <div
                  key={index}
                  className="flex flex-[0_0_100%] justify-center items-center min-w-0 embla__slide"
                >
                  <img
                    src={assetUrl + image}
                    alt=""
                    className="mx-auto sm:h-[40dvh]"
                  />
                </div>
              );
            })}
          </div>
        </div>
        {data.images.length > 1 && (
          <div className="right-0 bottom-3 left-0 z-20 absolute flex justify-center items-center gap-2">
            {data.images.map((_, index) => (
              <button
                key={index}
                className={cn(
                  "rounded-full size-2 cursor-pointer",
                  index === currentIndex ? "bg-white" : "bg-white/50"
                )}
                onClick={() => scrollTo(index)}
              />
            ))}
          </div>
        )}
        {data.images.length > 1 && (
          <>
            <button
              className="top-0 bottom-0 left-0 z-20 absolute flex items-center px-3"
              onClick={scrollPrev}
            >
              <div className="flex justify-center items-center bg-white rounded-full size-7 pointer-events-auto">
                <ChevronLeftIcon className="size-5 text-gray-800" />
              </div>
            </button>
            <button
              className="top-0 right-0 bottom-0 z-20 absolute flex items-center px-3"
              onClick={scrollNext}
            >
              <div className="flex justify-center items-center bg-white rounded-full size-7 pointer-events-auto">
                <ChevronRightIcon className="size-5 text-gray-800" />
              </div>
            </button>
          </>
        )}
      </div>
      <div className="mt-3">
        <div className="mb-2">
          <div className="flex sm:flex-row flex-col">
            <div className="flex-1">
              <h2 className="mb-2 font-montserrat font-semibold text-xl">
                {data.name}
              </h2>
              {!data.is_discount ? (
                <p className="font-bold text-xl">
                  Rp {Intl.NumberFormat("id").format(data.price.idr)}
                </p>
              ) : (
                <Fragment>
                  <p className="font-bold text-xl">
                    Rp{" "}
                    {Intl.NumberFormat("id").format(data.discount_amount.idr)}
                  </p>
                  <p className="line-through">
                    Rp {Intl.NumberFormat("id").format(data.price.idr)}
                  </p>
                </Fragment>
              )}
            </div>
            <div className="mt-3 sm:mt-0 pl-0 sm:pl-3">
              {isExistsOnCart ? (
                <Button
                  variant="blue"
                  roundFull
                  className="flex justify-center items-center w-full"
                  onClick={onCheckout}
                >
                  <CheckIcon className="size-4" /> Already in cart
                </Button>
              ) : !data.is_unlimited && data.stock <= 0 ? (
                <Button
                  variant="outline"
                  roundFull
                  className="flex justify-center items-center w-full"
                  disabled
                >
                  <ShoppingCartIcon className="size-4" /> Out of stock
                </Button>
              ) : (
                <Button
                  variant="green"
                  roundFull
                  className="flex justify-center items-center w-full font-medium"
                  onClick={onCheckout}
                >
                  <ShoppingCartIcon className="size-4" /> Checkout
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="[&_p]:mb-2 [&_p]:text-sm lg:[&_p]:text-base">
          <MarkdownFormat>{data.description}</MarkdownFormat>
        </div>

        <ShopItemUserReview data={data} />
      </div>
    </>
  );
}
